import { useCallback, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "../../../store/app/hooks"
import {
  setOpenAlert,
  setLoadingDelete,
  setCloseAlert,
} from "../../../store/features/alert/alertSlice"
import { useMutation, useQuery } from "@apollo/client"
import {
  selectUserPagination,
  selectUserQuery,
  setUserPagination,
  setQuery,
} from "../../../store/features/user/userSlice"
import { graphql } from "../../../gql"
import CustomTable from "../../Common/CustomTable/CustomTable"
import Loader from "../../Common/Loader/Loader"
import { getErrorsAsString } from "../../../utils/getErrorsAsString"
import { SortOrder, User, Role } from "../../../gql/graphql"
import { MRT_ColumnDef } from "material-react-table"
import { Pagination } from "../../../types/Pagination"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { useNavigate } from "react-router-dom"
import { setTitle } from "../../../store/features/menu/menuSlice"
import { updateCacheAdmin } from "../../../caches/updateCacheAdmin"
import { havePermissions } from "../../../utils/permissions"
import CustomImage from "../../Common/CustomImage/CustomImage"
import { getUserCountryId } from "../../../utils/authToken"
import { whereInputClause } from "../../../utils/permissions"
import useLang from "../../../hooks/useLang"

export const LIST_ADMINS = graphql(`
  query Admins(
    $where: UserWhereInput
    $aggregateUserWhere2: UserWhereInput
    $orderBy: [UserOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
  ) {
    users(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      phoneNumber
      email
      name
      lastName
      role
      isActiveAdmin
      ProfilePhoto {
        url
        id
      }
      country {
        id
        name_fr
      }
    }
    aggregateUser(where: $aggregateUserWhere2) {
      _count {
        _all
      }
    }
  }
`)

export const DELETE_ADMIN = graphql(`
  mutation DeleteOneAdmin($where: UserWhereUniqueInput!) {
    deleteOneUser(where: $where) {
      id
      phoneNumber
      email
      name
      lastName
      role
    }
  }
`)

const ListAdmin = () => {
  const { page, size } = useAppSelector(selectUserPagination)
  const query = useAppSelector(selectUserQuery)
  const dispatch = useAppDispatch()
  const countryId = getUserCountryId()
  const navigate = useNavigate()
  const { getText } = useLang()
  const queryName = useCallback(() => {
    return query
      ? {
          OR: [
            { name: { search: query } },
            { lastName: { search: query } },
            { email: { search: query } },
          ],
        }
      : {}
  }, [query])

  const { loading, data } = useQuery(LIST_ADMINS, {
    variables: {
      take: size,
      skip: page * size,
      orderBy: { name: SortOrder.Asc },
      where: {
        role: {
          equals: Role.Admin,
        },
        ...whereInputClause({
          country: {
            is: {
              id: {
                equals: countryId,
              },
            },
          },
        }),
        ...queryName(),
      },
    },
  })

  const [deleteAdmin] = useMutation(DELETE_ADMIN)

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorFn: (row) => (
          <>
            <CustomImage
              url={row?.ProfilePhoto?.url}
              style={{ width: "50px", height: "50px", borderRadius: "25px" }}
            />
          </>
        ),
        header: getText("Profile"),
        enableEditing: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
        size: 80,
        enableClickToCopy: true,
      },
      {
        accessorKey: "name",
        header: getText("Nom"),
        enableClickToCopy: true,
      },
      {
        accessorFn: (row) =>
          row?.country?.name_fr
            ? row?.country?.name_fr
            : getText("Pas définie"),
        header: getText("Pays"),
        enableEditing: false,
        enableColumnFilter: false,
      },
      {
        accessorFn: (row) =>
          row?.isActiveAdmin ? getText("Activé") : getText("Désactivé"),
        header: getText("Status"),
        enableEditing: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "lastName",
        header: getText("Prénom"),
        enableClickToCopy: true,
      },
      {
        accessorKey: "email",
        header: getText("Email"),
        enableClickToCopy: true,
      },
      {
        accessorKey: "phoneNumber",
        header: getText("Numéro de telephone"),
        enableClickToCopy: true,
      },
    ],
    [getText],
  )

  const handleChangePagination = (pagination: Pagination) =>
    dispatch(setUserPagination(pagination))

  const handleValidDelete = (id: number) => {
    dispatch(setLoadingDelete(true))
    deleteAdmin({
      variables: {
        where: {
          id,
        },
      },
      onCompleted: () => {
        dispatch(
          setOpenSnackbar({
            message: getText("L'admin a été supprimé avec succès"),
            status: "success",
          }),
        )
        dispatch(setLoadingDelete(false))
        dispatch(setCloseAlert())
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setCloseAlert())
        dispatch(setOpenSnackbar({ message }))
      },
      update: (cache, { data }) => {
        updateCacheAdmin({
          action: "delete",
          cache,
          entryData: data?.deleteOneUser,
        })
      },
    })
  }

  const handleOpenDeleteDialog = (id: number) => {
    dispatch(
      setOpenAlert({
        handleValid: () => handleValidDelete(id),
        message: getText(
          "Êtes-vous vraiment sûr de vouloir supprimer cet admin ?",
        ),
        isLoading: false,
      }),
    )
  }

  const onChangeSearchValue = (value: string) => dispatch(setQuery(value))

  const handleEdit = (id: number) => {
    dispatch(setTitle(getText("Modifier un admin")))
    navigate(`/admin/edit/${id}`)
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  if (loading && !query) return <Loader />

  return (
    <CustomTable
      columns={columns}
      data={data?.users || []}
      lableAddNew={getText("Créer une nouveau admin")}
      rootLisName={"/admin/add"}
      isLoading={loading}
      showProgressBars={loading}
      rowCount={data?.aggregateUser._count?._all || 0}
      handleChangePagination={handleChangePagination}
      pageIndex={page}
      pageSize={size}
      searchValue={query}
      onChangeSearchValue={onChangeSearchValue}
      handleDelete={
        havePermissions(["admin.delete"]) ? handleOpenDeleteDialog : undefined
      }
      isHaveHeaderAction={havePermissions(["admin.create"])}
      handleEdit={havePermissions(["admin.change"]) ? handleEdit : undefined}
      isHaveAction={havePermissions(["admin.change", "admin.delete"])}
      handleRefresh={handleRefresh}
    />
  )
}

export default ListAdmin
